import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik, FormikHelpers, isEmptyArray, useFormikContext } from 'formik'
import * as Yup from 'yup'

import { ProductIds } from 'core'
import { CoverageProviderResponse, getCoverageProviders } from 'core/application/expenses/getCoverageProviders'
import saveCoverageProvider, {
  SaveCoverageProviderRequest,
  SaveCoverageProviderResponse,
} from 'core/application/expenses/saveCoverageProvider'

import Option from 'common/components/dataEntry/components/Option'
import { DatePicker, Input, Select } from 'common/components/dataEntry/formik'
import Modal from 'common/components/feedback/Modal'
import Button from 'common/components/general/Button'

import useCommandRequest from 'common/hooks/useCommandRequest'
import useQueryRequest from 'common/hooks/useQueryRequest'

import { parseZone, parseZoneFormat, parseZoneMonthYearFormat } from 'common/dateFormatters'
import { useLoginUserV2 } from 'common/hooks'

import './styles.less'

type CoverageProviderSelectProps = {
  name: string
  date: string
  familyMemberId?: string
  planPeriodId: string
}

const CoverageProviderSelect: React.FC<CoverageProviderSelectProps> = (props) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const [coverageProviders, loadingCoverageProviders, sendGetCoverageProviders] = useQueryRequest<
    CoverageProviderResponse[]
  >([])

  const [showModal, setShowModal] = useState(false)
  const [initialValues, setInitialValues] = useState({})
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [sendCommand, isCommandLoading] = useCommandRequest<SaveCoverageProviderResponse[]>()
  const {
    user: { productId },
  } = useLoginUserV2()

  const context = useFormikContext<any>()

  useEffect(() => {
    ;(async () => {
      const result = await sendGetCoverageProviders(() =>
        getCoverageProviders(parseZoneFormat(props.date), props.familyMemberId),
      )
      const existingCoverageProvider = result.find((provider) => provider.id === context.values?.coverageProvider)
      context.setFieldValue(props.name, existingCoverageProvider?.id || null)
    })()
  }, [props.date, props.familyMemberId])

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const request: SaveCoverageProviderRequest = {
      familyMemberId: props.familyMemberId,
      providerName: values.providerName,
      providerStartDate: values.providerStartDate,
    }
    const result = await sendCommand(() => saveCoverageProvider(request))
    if (result.completed) {
      await sendGetCoverageProviders(() => getCoverageProviders(props.date, props.familyMemberId))

      const purchaseDate = parseZone(props.date)
      const found = result.data.find((x) => {
        const startDate = parseZone(x.startDate)
        return startDate.year() === purchaseDate.year() && startDate.month() === purchaseDate.month()
      })
      if (found) {
        context.setFieldValue(props.name, found.id)
      }
      setShowModal(false)
      formikHelpers.setValues({}, false)
    }
  }

  const coverageProviderAddModal = () => (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        setIsFormValid(formValidator(translate).isValidSync(values))
      }}
    >
      {(formik) => {
        return (
          <form autoComplete="off">
            <Modal
              title={'Add Coverage Provider'}
              visible={showModal}
              destroyOnClose
              closable
              onCancel={() => {
                setShowModal(false)
              }}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setShowModal(false)
                  }}
                >
                  {translate('translation:general.cancel')}
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  disabled={!isFormValid}
                  loading={isCommandLoading}
                  onClick={formik.submitForm}
                >
                  {translate('translation:general.add')}
                </Button>,
              ]}
            >
              <div className={'mb-16'}>
                <Input
                  showRequiredSign={true}
                  label={'Provider Name'}
                  inputCustomStyle="mt-12"
                  name={`providerName`}
                  placeholder={'Provider name'}
                />
              </div>

              <div className={'mb-16'}>
                <DatePicker
                  showRequiredSign={true}
                  label={'Provider start date'}
                  labelBlock={true}
                  labelStyle="mb-12"
                  placeholder={'Select provider start date'}
                  name={`providerStartDate`}
                  id="ProviderStartDate"
                  disabledDate={(d) => !d || d.isAfter(parseZone(props.date).endOf('month'))}
                />
              </div>
            </Modal>
          </form>
        )
      }}
    </Formik>
  )
  return (
    <>
      {!loadingCoverageProviders && (
        <Select
          label={'Coverage Provider'}
          labelStyles="mb-12"
          name={props.name}
          placeholder={'Select a coverage provider'}
          showRequiredSign={true}
          dropdownRender={(menu) => (
            <>
              {!isEmptyArray(coverageProviders) && menu}
              <Button
                className="w-100 flex flex__align_center"
                type="text"
                icon="fa fa-plus"
                onClick={() => setShowModal(true)}
              >
                {`Add coverage provider`}
              </Button>
            </>
          )}
        >
          {coverageProviders.map((item: CoverageProviderResponse) => (
            <Option key={item.id} value={item.id}>
              {`${item.name}(${parseZoneMonthYearFormat(item.providerStartDate)})`}
            </Option>
          ))}
        </Select>
      )}

      {coverageProviderAddModal()}
    </>
  )
}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    providerName: Yup.string().nullable().required(translate('translation:general.required')),
    providerStartDate: Yup.date()
      .nullable()
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
  })

export default CoverageProviderSelect

export const ADD_COVERAGE_PROVIDER: string = 'add'
